import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Plus, Trash2, Pencil, Check, ChevronDown, ChevronRight,
  Play, CheckCircle, Pause, Paperclip, FileText, Calendar, Users, Wrench,
  AlertTriangle
} from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { AllegatiModal } from '../../../components/allegati/AllegatiModal'
import FaseAllegatiModal from '../../../components/allegati/FaseAllegatiModal'
import { OwnershipBadge } from '../../../components/OwnershipBadge'
import { TransferOwnershipModal } from '../../../components/TransferOwnershipModal'
import { GanttChart } from '../../../components/commesse/GanttChart'
import SchedePerEntita from '../../../components/schede/SchedePerEntita'
import {
  CommessaDetail, StatoCommessa, FaseDetail, FaseForm, GiornoFaseForm,
  PersonaleMinimal, StrumentoMinimal
} from '../../../types/commesse'

interface CostiFase {
  fase_id: string | null
  fase_label: string
  giorni_effettivi: number
  costo_personale: number
  costo_mezzi: number
  costo_totale: number
}

interface CostiCommessa {
  commessa_id: string
  totale_costo: number
  fasi: CostiFase[]
}

const statoBadge: Record<StatoCommessa, { label: string; bg: string; text: string }> = {
  pianificazione: { label: 'Pianificazione', bg: 'bg-gray-100', text: 'text-gray-700' },
  in_revisione: { label: 'In Revisione', bg: 'bg-purple-100', text: 'text-purple-700' },
  approvata: { label: 'Approvata', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  in_esecuzione: { label: 'In Esecuzione', bg: 'bg-blue-100', text: 'text-blue-700' },
  completata: { label: 'Completata', bg: 'bg-green-100', text: 'text-green-700' },
  sospesa: { label: 'Sospesa', bg: 'bg-amber-100', text: 'text-amber-700' },
  rifiutata: { label: 'Rifiutata', bg: 'bg-red-100', text: 'text-red-700' },
}

export default function CommessaDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [commessa, setCommessa] = useState<CommessaDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [personale, setPersonale] = useState<PersonaleMinimal[]>([])
  const [strumenti, setStrumenti] = useState<StrumentoMinimal[]>([])

  const [costiCalendario, setCostiCalendario] = useState<CostiCommessa | null>(null)
  const [showTransfer, setShowTransfer] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [showAllegati, setShowAllegati] = useState(false)
  const [faseAllegati, setFaseAllegati] = useState<{ id: string; titolo: string } | null>(null)
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null)

  // Fase form
  const [showFaseForm, setShowFaseForm] = useState(false)
  const [editingFase, setEditingFase] = useState<FaseDetail | null>(null)
  const [faseForm, setFaseForm] = useState<FaseForm>({
    titolo: '', durata_prevista: 1, responsabile_id: '', tipo: 'sequenziale',
    partecipanti_ids: [], strumenti_ids: []
  })

  // Giorno form
  const [addingGiornoFaseId, setAddingGiornoFaseId] = useState<string | null>(null)
  const [giornoForm, setGiornoForm] = useState<GiornoFaseForm>({
    data: new Date().toISOString().split('T')[0],
    partecipanti_ids: [],
    strumenti_ids: [],
    note: ''
  })

  // Expanded fasi
  const [expandedFasi, setExpandedFasi] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      const [commessaRes, personaleRes, strumentiRes] = await Promise.all([
        api.get(`/v1/commesse/${id}`),
        api.get('/v1/personale'),
        api.get('/v1/strumenti')
      ])
      setCommessa(commessaRes.data)
      setPersonale(personaleRes.data.map((p: any) => ({
        id: p.id, nome: p.nome, cognome: p.cognome
      })))
      setStrumenti(strumentiRes.data.map((s: any) => ({
        id: s.id, descrizione: s.descrizione
      })))
      // Carica costi dal calendario
      try {
        const costiRes = await api.get(`/v1/calendario/costi-commessa/${id}`)
        setCostiCalendario(costiRes.data)
      } catch { /* calendario non ancora configurato */ }
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStato = async (newStato: StatoCommessa) => {
    if (!commessa) return
    try {
      let res
      if (newStato === 'in_esecuzione' && commessa.stato === 'pianificazione') {
        // Usa endpoint dedicato /avvia
        res = await api.post(`/v1/commesse/${commessa.id}/avvia`)
        setCommessa(res.data)
        if (res.data.avviso_ritardo) {
          setToast({ message: res.data.avviso_ritardo, type: 'warning' })
        } else {
          setToast({ message: 'Commessa avviata', type: 'success' })
        }
      } else {
        const updates: any = { stato: newStato }
        if (newStato === 'completata') {
          updates.data_completamento = new Date().toISOString().split('T')[0]
        }
        res = await api.put(`/v1/commesse/${commessa.id}`, updates)
        setCommessa(res.data)
        setToast({ message: 'Stato aggiornato', type: 'success' })
      }
    } catch {
      setToast({ message: 'Errore aggiornamento stato', type: 'error' })
    }
    setConfirmAction(null)
  }

  // ========== FASI ==========

  const handleCreateFase = async () => {
    if (!commessa || !faseForm.titolo) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/fasi`, {
        titolo: faseForm.titolo,
        durata_prevista: faseForm.durata_prevista,
        responsabile_id: faseForm.responsabile_id || commessa.responsabile_id,
        tipo: faseForm.tipo,
      })
      setCommessa(res.data)
      setShowFaseForm(false)
      resetFaseForm()
      setToast({ message: 'Fase creata', type: 'success' })
    } catch {
      setToast({ message: 'Errore creazione fase', type: 'error' })
    }
  }

  const handleUpdateFase = async () => {
    if (!commessa || !editingFase || !faseForm.titolo) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/fasi/${editingFase.id}`, {
        titolo: faseForm.titolo,
        durata_prevista: faseForm.durata_prevista,
        responsabile_id: faseForm.responsabile_id,
        tipo: faseForm.tipo,
      })
      setCommessa(res.data)
      setEditingFase(null)
      setShowFaseForm(false)
      resetFaseForm()
      setToast({ message: 'Fase aggiornata', type: 'success' })
    } catch {
      setToast({ message: 'Errore aggiornamento fase', type: 'error' })
    }
  }

  const handleToggleFaseCompletata = async (fase: FaseDetail) => {
    if (!commessa) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/fasi/${fase.id}`, {
        completata: !fase.completata,
      })
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore aggiornamento fase', type: 'error' })
    }
  }

  const handleDeleteFase = async (faseId: string) => {
    if (!commessa) return
    try {
      await api.delete(`/v1/commesse/${commessa.id}/fasi/${faseId}`)
      setCommessa(prev => prev ? {
        ...prev,
        fasi: prev.fasi.filter(f => f.id !== faseId),
        fasi_totali: prev.fasi_totali - 1,
      } : null)
      setToast({ message: 'Fase eliminata', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione fase', type: 'error' })
    }
    setConfirmAction(null)
  }

  const resetFaseForm = () => {
    setFaseForm({
      titolo: '', durata_prevista: 1,
      responsabile_id: commessa?.responsabile_id || '',
      tipo: 'sequenziale',
      partecipanti_ids: [], strumenti_ids: []
    })
  }

  const startEditFase = (fase: FaseDetail) => {
    setEditingFase(fase)
    setFaseForm({
      titolo: fase.titolo,
      durata_prevista: fase.durata_prevista,
      responsabile_id: fase.responsabile_id,
      tipo: fase.tipo,
      partecipanti_ids: fase.partecipanti_ids || [],
      strumenti_ids: fase.strumenti_ids || [],
    })
    setShowFaseForm(true)
  }

  // ========== GIORNI ==========

  const handleCreateGiorno = async (faseId: string) => {
    if (!commessa) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/fasi/${faseId}/giorni`, giornoForm)
      setCommessa(res.data)
      setAddingGiornoFaseId(null)
      setGiornoForm({
        data: new Date().toISOString().split('T')[0],
        partecipanti_ids: [],
        strumenti_ids: [],
        note: ''
      })
      setToast({ message: 'Giorno aggiunto', type: 'success' })
    } catch {
      setToast({ message: 'Errore aggiunta giorno', type: 'error' })
    }
  }

  const handleDeleteGiorno = async (faseId: string, giornoId: string) => {
    if (!commessa) return
    try {
      await api.delete(`/v1/commesse/${commessa.id}/fasi/${faseId}/giorni/${giornoId}`)
      // Ricarica
      const res = await api.get(`/v1/commesse/${commessa.id}`)
      setCommessa(res.data)
      setToast({ message: 'Giorno eliminato', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione giorno', type: 'error' })
    }
  }

  const toggleExpandFase = (faseId: string) => {
    setExpandedFasi(prev => {
      const next = new Set(prev)
      if (next.has(faseId)) next.delete(faseId)
      else next.add(faseId)
      return next
    })
  }

  const togglePartecipante = (personaleId: string) => {
    setGiornoForm(prev => ({
      ...prev,
      partecipanti_ids: prev.partecipanti_ids.includes(personaleId)
        ? prev.partecipanti_ids.filter(id => id !== personaleId)
        : [...prev.partecipanti_ids, personaleId]
    }))
  }

  const toggleStrumento = (strumentoId: string) => {
    setGiornoForm(prev => ({
      ...prev,
      strumenti_ids: prev.strumenti_ids.includes(strumentoId)
        ? prev.strumenti_ids.filter(id => id !== strumentoId)
        : [...prev.strumenti_ids, strumentoId]
    }))
  }

  const getPersonaleNome = (id: string) => {
    const p = personale.find(p => p.id === id)
    return p ? `${p.cognome} ${p.nome}` : id.substring(0, 8)
  }

  const getStrumentoNome = (id: string) => {
    const s = strumenti.find(s => s.id === id)
    return s ? s.descrizione : id.substring(0, 8)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Caricamento...</div>
      </div>
    )
  }

  if (!commessa) {
    return <div className="text-center text-gray-500 py-8">Commessa non trovata</div>
  }

  const badge = statoBadge[commessa.stato]
  const today = new Date().toISOString().split('T')[0]
  const isOverdue = commessa.data_prevista_consegna < today && commessa.stato !== 'completata'

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText="Conferma"
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />

      <AllegatiModal
        isOpen={showAllegati}
        onClose={() => setShowAllegati(false)}
        entityType="commessa"
        entityId={commessa.id}
      />

      {faseAllegati && (
        <FaseAllegatiModal
          faseId={faseAllegati.id}
          faseTitolo={faseAllegati.titolo}
          onClose={() => setFaseAllegati(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/app/commesse')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
              {commessa.numero_commessa}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
              {badge.label}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{commessa.titolo}</h1>
          <p className="text-gray-500">{commessa.cliente_nome} - Resp: {commessa.responsabile_nome}</p>
        </div>

        {/* Azioni */}
        <div className="flex items-center gap-2">
          <OwnershipBadge
            ownerId={commessa.owner_id}
            ownerName={commessa.owner_name}
            onTransfer={() => setShowTransfer(true)}
          />
          <TransferOwnershipModal
            isOpen={showTransfer}
            onClose={() => setShowTransfer(false)}
            entityType="commessa"
            entityId={commessa.id}
            currentOwnerId={commessa.owner_id}
            onTransferred={() => {
              setToast({ message: 'Proprietà trasferita', type: 'success' })
              loadData()
            }}
          />
          {commessa.stato === 'pianificazione' && (
            <button
              onClick={() => setConfirmAction({
                title: 'Avvia Commessa',
                message: 'Vuoi avviare l\'esecuzione della commessa?',
                onConfirm: () => handleUpdateStato('in_esecuzione')
              })}
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50"
            >
              <Play className="w-4 h-4" />
              Avvia
            </button>
          )}
          {commessa.stato === 'in_esecuzione' && (
            <>
              <button
                onClick={() => setConfirmAction({
                  title: 'Completa Commessa',
                  message: 'Vuoi segnare la commessa come completata?',
                  onConfirm: () => handleUpdateStato('completata')
                })}
                className="flex items-center gap-2 px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50"
              >
                <CheckCircle className="w-4 h-4" />
                Completa
              </button>
              <button
                onClick={() => setConfirmAction({
                  title: 'Sospendi Commessa',
                  message: 'Vuoi sospendere la commessa?',
                  onConfirm: () => handleUpdateStato('sospesa')
                })}
                className="flex items-center gap-2 px-3 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50"
              >
                <Pause className="w-4 h-4" />
              </button>
            </>
          )}
          {commessa.stato === 'sospesa' && (
            <button
              onClick={() => handleUpdateStato('in_esecuzione')}
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50"
            >
              <Play className="w-4 h-4" />
              Riprendi
            </button>
          )}
          <button
            onClick={() => setShowAllegati(true)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/app/offerte/${commessa.offerta_id}`)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            title="Vai all'offerta"
          >
            <FileText className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Ore Offerta</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{commessa.ore_previste_offerta || commessa.giorni_previsti}</p>
        </div>
        <div className={`bg-white rounded-xl shadow-sm p-4 ${
          commessa.ore_gantt_totali > (commessa.ore_previste_offerta || commessa.giorni_previsti) && (commessa.ore_previste_offerta || commessa.giorni_previsti) > 0
            ? 'ring-2 ring-red-400' : ''
        }`}>
          <p className="text-xs text-gray-500 uppercase font-medium">Ore Gantt</p>
          <p className={`text-2xl font-bold mt-1 ${
            commessa.ore_gantt_totali > (commessa.ore_previste_offerta || commessa.giorni_previsti) && (commessa.ore_previste_offerta || commessa.giorni_previsti) > 0
              ? 'text-red-600' : 'text-gray-900'
          }`}>
            {commessa.ore_gantt_totali}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Giorni Svolti</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{commessa.giorni_svolti}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Fasi</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {commessa.fasi_completate}/{commessa.fasi_totali}
          </p>
        </div>
        <div className={`bg-white rounded-xl shadow-sm p-4 ${isOverdue ? 'ring-2 ring-red-400' : ''}`}>
          <p className="text-xs text-gray-500 uppercase font-medium">Consegna</p>
          <p className={`text-lg font-bold mt-1 ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
            {formatDate(commessa.data_prevista_consegna)}
          </p>
          {isOverdue && <p className="text-xs text-red-500">In ritardo</p>}
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Costo Cal.</p>
          <p className="text-xl font-bold text-gray-900 mt-1">
            {costiCalendario && costiCalendario.totale_costo > 0
              ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(costiCalendario.totale_costo)
              : '—'
            }
          </p>
        </div>
      </div>

      {/* Avviso ore */}
      {commessa.avviso_ore && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Attenzione Ore</p>
            <p className="text-sm text-amber-700">{commessa.avviso_ore}</p>
          </div>
        </div>
      )}

      {/* Gantt Chart */}
      <div className="mb-6">
        <GanttChart
          fasi={commessa.fasi}
          dataInizio={commessa.data_inizio}
          dataConsegna={commessa.data_prevista_consegna}
          stato={commessa.stato}
        />
      </div>

      {/* Fasi Section */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Fasi di Lavoro</h2>
          {commessa.stato !== 'completata' && (
            <button
              onClick={() => {
                resetFaseForm()
                setEditingFase(null)
                setShowFaseForm(true)
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              Nuova Fase
            </button>
          )}
        </div>

        {/* Fase Form (create/edit) */}
        {showFaseForm && (
          <div className="p-5 border-b border-dashed border-gray-300 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {editingFase ? 'Modifica Fase' : 'Nuova Fase'}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Titolo fase *</label>
                <input
                  type="text"
                  list="fase-titoli-suggeriti"
                  placeholder="Seleziona o scrivi..."
                  value={faseForm.titolo}
                  onChange={e => setFaseForm(prev => ({ ...prev, titolo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
                <datalist id="fase-titoli-suggeriti">
                  <option value="Analisi iniziale" />
                  <option value="Progettazione preliminare" />
                  <option value="Progettazione esecutiva e distinta materiali" />
                  <option value="Approvvigionamenti" />
                  <option value="Produzione interna" />
                  <option value="Installazione in loco" />
                  <option value="Collaudi e certificazioni" />
                  <option value="Consegna finale" />
                  <option value="Assistenza post-vendita" />
                </datalist>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Durata (giorni) *</label>
                <input
                  type="number"
                  min="1"
                  value={faseForm.durata_prevista}
                  onChange={e => setFaseForm(prev => ({ ...prev, durata_prevista: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                <select
                  value={faseForm.tipo}
                  onChange={e => setFaseForm(prev => ({ ...prev, tipo: e.target.value as 'sequenziale' | 'parallelo' }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  <option value="sequenziale">Sequenziale</option>
                  <option value="parallelo">Parallelo</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Responsabile</label>
                <select
                  value={faseForm.responsabile_id}
                  onChange={e => setFaseForm(prev => ({ ...prev, responsabile_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  <option value="">Default commessa</option>
                  {personale.map(p => (
                    <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
                  ))}
                </select>
              </div>
              {/* Partecipanti multi-select */}
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Personale assegnato</label>
                <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-white">
                  {personale.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setFaseForm(prev => ({
                          ...prev,
                          partecipanti_ids: prev.partecipanti_ids.includes(p.id)
                            ? prev.partecipanti_ids.filter(id => id !== p.id)
                            : [...prev.partecipanti_ids, p.id]
                        }))
                      }}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                        faseForm.partecipanti_ids.includes(p.id)
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                      }`}
                    >
                      {p.cognome} {p.nome}
                    </button>
                  ))}
                </div>
              </div>

              {/* Strumenti multi-select */}
              {strumenti.length > 0 && (
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Strumenti / Mezzi</label>
                  <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-white">
                    {strumenti.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setFaseForm(prev => ({
                            ...prev,
                            strumenti_ids: prev.strumenti_ids.includes(s.id)
                              ? prev.strumenti_ids.filter(id => id !== s.id)
                              : [...prev.strumenti_ids, s.id]
                          }))
                        }}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                          faseForm.strumenti_ids.includes(s.id)
                            ? 'bg-amber-100 border-amber-300 text-amber-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300'
                        }`}
                      >
                        {s.descrizione}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="col-span-4 flex gap-2 justify-end">
                <button
                  onClick={() => { setShowFaseForm(false); setEditingFase(null) }}
                  className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm"
                >
                  Annulla
                </button>
                <button
                  onClick={editingFase ? handleUpdateFase : handleCreateFase}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  {editingFase ? 'Salva' : 'Aggiungi'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Fasi List */}
        {commessa.fasi.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Nessuna fase definita</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {commessa.fasi.map((fase) => {
              const isExpanded = expandedFasi.has(fase.id)
              const today = new Date().toISOString().split('T')[0]

              return (
                <div key={fase.id}>
                  {/* Fase Header */}
                  <div
                    className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleExpandFase(fase.id)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}

                    {/* Checkbox completata */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleFaseCompletata(fase) }}
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        fase.completata
                          ? 'bg-green-500 border-green-500 text-white'
                          : fase.in_ritardo
                          ? 'border-red-400 hover:border-red-500'
                          : 'border-gray-300 hover:border-primary-500'
                      }`}
                    >
                      {fase.completata && <Check className="w-3 h-3" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${fase.completata ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                          {fase.titolo}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          fase.tipo === 'parallelo' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {fase.tipo === 'parallelo' ? 'Par.' : 'Seq.'}
                        </span>
                        {fase.in_ritardo && (
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400">
                        {fase.responsabile_nome} - {fase.giorni_effettivi}/{fase.durata_prevista} giorni
                        {fase.partecipanti_ids?.length > 0 && (
                          <span className="ml-2 text-blue-500">
                            {fase.partecipanti_ids.length} pers.
                          </span>
                        )}
                        {fase.strumenti_ids?.length > 0 && (
                          <span className="ml-1 text-amber-500">
                            {fase.strumenti_ids.length} strum.
                          </span>
                        )}
                        {(() => {
                          const costoFase = costiCalendario?.fasi?.find(cf => cf.fase_id === fase.id)
                          return costoFase && costoFase.costo_totale > 0 ? (
                            <span className="ml-2 text-green-600 font-medium">
                              {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(costoFase.costo_totale)}
                            </span>
                          ) : null
                        })()}
                        {fase.in_ritardo && <span className="text-red-500 ml-1">(in ritardo)</span>}
                      </p>
                    </div>

                    {/* Barra mini progress */}
                    <div className="w-20 flex-shrink-0">
                      <div className="bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            fase.completata ? 'bg-green-500' :
                            fase.in_ritardo ? 'bg-red-500' :
                            fase.giorni_effettivi > 0 ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                          style={{ width: `${Math.min(100, fase.durata_prevista > 0 ? (fase.giorni_effettivi / fase.durata_prevista) * 100 : 0)}%` }}
                        />
                      </div>
                    </div>

                    {/* Azioni */}
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setFaseAllegati({ id: fase.id, titolo: fase.titolo })}
                        className="p-1.5 text-gray-400 hover:text-amber-600 rounded transition-colors"
                        title="Documenti fase"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => startEditFase(fase)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmAction({
                          title: 'Elimina Fase',
                          message: `Eliminare la fase "${fase.titolo}" e tutti i suoi giorni?`,
                          onConfirm: () => handleDeleteFase(fase.id)
                        })}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Fase Expanded Content */}
                  {isExpanded && (
                    <div className="bg-gray-50 border-t border-gray-100 px-5 py-3">
                      {/* Giorni list */}
                      {fase.giorni.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {fase.giorni.map(giorno => {
                            const isPast = giorno.data <= today
                            return (
                              <div
                                key={giorno.id}
                                className={`flex items-start gap-3 p-2.5 rounded-lg text-sm ${
                                  isPast ? 'bg-white border border-green-200' : 'bg-white border border-gray-200'
                                }`}
                              >
                                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isPast ? 'bg-green-500' : 'bg-blue-400'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">
                                      {formatDate(giorno.data)}
                                    </span>
                                    <span className={`text-xs ${isPast ? 'text-green-600' : 'text-blue-600'}`}>
                                      {isPast ? 'Svolto' : 'Previsto'}
                                    </span>
                                  </div>
                                  {giorno.partecipanti_ids.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap mb-1">
                                      <Users className="w-3 h-3 text-gray-400" />
                                      {giorno.partecipanti_ids.map(pid => (
                                        <span key={pid} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                                          {getPersonaleNome(pid)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {giorno.strumenti_ids.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <Wrench className="w-3 h-3 text-gray-400" />
                                      {giorno.strumenti_ids.map(sid => (
                                        <span key={sid} className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                                          {getStrumentoNome(sid)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {giorno.note && (
                                    <p className="text-xs text-gray-500 mt-1">{giorno.note}</p>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteGiorno(fase.id, giorno.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded flex-shrink-0"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Add Giorno Form */}
                      {addingGiornoFaseId === fase.id ? (
                        <div className="border border-dashed border-primary-300 rounded-lg p-3 bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Data *</label>
                              <input
                                type="date"
                                value={giornoForm.data}
                                onChange={e => setGiornoForm(prev => ({ ...prev, data: e.target.value }))}
                                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Note</label>
                              <input
                                type="text"
                                placeholder="Note opzionali..."
                                value={giornoForm.note}
                                onChange={e => setGiornoForm(prev => ({ ...prev, note: e.target.value }))}
                                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                          </div>

                          {/* Partecipanti multi-select */}
                          <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Partecipanti</label>
                            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                              {personale.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => togglePartecipante(p.id)}
                                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                    giornoForm.partecipanti_ids.includes(p.id)
                                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                                      : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                                  }`}
                                >
                                  {p.cognome} {p.nome}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Strumenti multi-select */}
                          {strumenti.length > 0 && (
                            <div className="mb-3">
                              <label className="block text-xs text-gray-500 mb-1">Strumenti / Mezzi</label>
                              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                {strumenti.map(s => (
                                  <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => toggleStrumento(s.id)}
                                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                      giornoForm.strumenti_ids.includes(s.id)
                                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300'
                                    }`}
                                  >
                                    {s.descrizione}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setAddingGiornoFaseId(null)}
                              className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                            >
                              Annulla
                            </button>
                            <button
                              onClick={() => handleCreateGiorno(fase.id)}
                              className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                            >
                              Aggiungi Giorno
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setAddingGiornoFaseId(fase.id)
                            setGiornoForm({
                              data: new Date().toISOString().split('T')[0],
                              partecipanti_ids: [],
                              strumenti_ids: [],
                              note: ''
                            })
                          }}
                          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 py-1"
                        >
                          <Plus className="w-4 h-4" />
                          Aggiungi giorno di lavoro
                        </button>
                      )}

                      {/* Link Calendario */}
                      <button
                        onClick={() => navigate('/app/calendario')}
                        className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 py-1 mt-2"
                      >
                        <Calendar className="w-4 h-4" />
                        Vedi in Calendario
                      </button>

                      {/* Schede Dinamiche */}
                      <SchedePerEntita
                        entityType="fase_commessa"
                        entityId={fase.id}
                        entityLabel={`${commessa!.numero_commessa} - ${fase.titolo}`}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
